import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../../../layouts/Default";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Title } from "../../../../themes/Default/components/Title";
import { Main } from "../../../../themes/Default/components/Main";
import { ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { Slide, ToastContainer } from "react-toastify";
import { Month } from "../../../../themes/Default/components/Calendar/components/Month";
import { Day, DayOffset } from "../../../../themes/Default/components/Calendar/components/Month/components/Day";
import { Calendar } from "../../../../themes/Default/components/Calendar";
import { groupsManagement } from "../../../../hooks/api/GroupsManagement";
import { eachDayOfInterval, eachMonthOfInterval, endOfMonth, format, startOfMonth } from "date-fns";
import { useUserToken } from "../../../../hooks/core/UserToken";
import { roles } from "../../../../services/utils/Roles";

const Planning = () => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const { groupId } = useParams();
    const [dates, setDates] = useState([]);
    const [plans, setPlans] = useState([]);
    const [months, setMonths] = useState([]);
    const [groupName, setGroupName] = useState("");
    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();
    const { useListGroupDatesMutation, usePlansListMutation, useViewGroupMutation } = groupsManagement;

    const [listGroupDates] = useListGroupDatesMutation();
    const [listGroupPlans] = usePlansListMutation();
    const [viewGroup] = useViewGroupMutation();
    const [hasTests, setHasTests] = useState(false);
    const [hasProgress, setHasProgress] = useState(false);
    const [hasReport, setHasReport] = useState(false);

    const tabs = [
        { title: t("groups.attendance"), onClick: () => gotoAttendanceTab(groupId) },
        { title: t("groups.planning"), isActive: true },
        ...(hasProgress ? [
            { title: t('groups.progress'), onClick: () => gotoProgressTab(groupId)},
        ] : []), 
        ...(hasTests ? [
            { title: t('groups.progressTest'), onClick: () => gotoProgressTestsTab(groupId) }
        ] : []), 
        ...(hasReport ? [
            { title: t('reports.title'),onClick: () => gotoReportTab(groupId)},
        ] : []),
        { title: t("documents"), onClick: () => gotoDocumentsTab(groupId) },
        { title: t("notes"), onClick: () => gotoNotesTab(groupId) },
    ];

    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();
    if (
        decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
        decodedToken?.roles.includes(roles.SECRETARY) ||
        decodedToken?.roles.includes(roles.DIRECTOR) || 
        decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES)
    ) {
        tabs.push({
            title: t("settings.title"),
            onClick: () => gotoEditTab(groupId),
        });
    }

    const gotoAttendanceTab = (groupId) => {
        navigate("/groups/" + groupId + "/attendance");
    };

    const gotoProgressTestsTab = (groupId) => {
        navigate("/groups/" + groupId + "/tests");
    }

    const gotoReportTab = (groupId) => {
        navigate("/groups/" + groupId + "/reports");
    }

    const gotoNotesTab = (groupId) => {
        navigate("/groups/" + groupId + "/notes");
    };

    const gotoDocumentsTab = (groupId) => {
        navigate("/groups/" + groupId + "/documents");
    };

    const gotoProgressTab = (groupId) => {
        navigate("/groups/" + groupId + "/progress");
    };

    const gotoEditTab = (groupId) => {
        navigate("/groups/edit/" + groupId);
    };

    const gotoAdd = (dateId, index) => {
        navigate(`/groups/${groupId}/planning/${dateId}/add/classIndex/${index}`);
    };

    const gotoEdit = (dateId, planId, index) => {
        navigate(`/groups/${groupId}/planning/${dateId}/edit/${planId}/classIndex/${index}`);
    };

    const isCancel = (dateId) => {
        return dates.some(date => date.id === dateId && date.isCancel === true);
    }

    const isSubstitute = (dateId) => {
        return plans.some(plan => plan.dateId === dateId && plan.substituteProfessor !== null);
    }

    const monthNames = [
        t("calendar.january"),
        t("calendar.february"),
        t("calendar.march"),
        t("calendar.april"),
        t("calendar.may"),
        t("calendar.june"),
        t("calendar.july"),
        t("calendar.august"),
        t("calendar.september"),
        t("calendar.october"),
        t("calendar.november"),
        t("calendar.december"),
    ];

    const loadMonths = () => {
        listGroupDates(groupId).then((result) => {
            setDates(result?.data?.records);
            const months = eachMonthOfInterval({
                start: startOfMonth(startDate),
                end: endOfMonth(endDate),
            });
            setMonths(months);
        });
    };

    const loadPlans = () => {
        listGroupPlans(groupId).then((result) => {
            setPlans(result?.data?.records);
        });
    };

    const loadGroupName = () => {
        viewGroup(groupId).then((result) => {
            setGroupName(result?.data?.name);
            setHasTests(result?.data?.hasTests);
            setHasReport(result?.data?.hasReport);
            setHasProgress(result?.data?.hasProgress);
            setStartDate(result?.data?.startDate ? new Date(result.data.startDate) : new Date("2025-01-01"));
            setEndDate(result?.data?.endDate ? new Date(result.data.endDate) : new Date("2025-12-31"));
        });
    };

    useEffect(() => {
        loadGroupName();
    }, []);

    useEffect(() => {
        if (startDate) {
            loadMonths();
        }
        loadPlans();
    }, [startDate]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to={"/groups"} />
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{groupName}</Title>
                <Breadcrumps>
                    <Link title={t("groups.title")} icon="fa-light fa-users" />
                    <Link title={t("groups.planning")} isActive={true} />
                </Breadcrumps>
                <Calendar>
                    {months.map((month, index) => {
                        const days = eachDayOfInterval({
                            start: startOfMonth(month),
                            end: endOfMonth(month),
                        });

                        return (
                            <Month month={monthNames[month.getMonth()]} year={month.getFullYear()} key={index}>
                                <DayOffset days={days} />
                                {days.map((day, index) => {
                                    const formattedDate = `${day.getFullYear()}-${(day.getMonth() + 1).toString().padStart(2, "0")}-${day.getDate().toString().padStart(2, "0")}`;
                                    let selectedPlan = null;
                                    for (const plan of plans) {
                                        if (plan.date === formattedDate) {
                                            selectedPlan = plan;
                                            break;
                                        }
                                    }

                                    let isWeekend = day.getDay() === 6 || day.getDay() === 0;
                                    let isSelectedDash = dates.some((item) => item.date === format(day, "yyyy-MM-dd"));
                                    let isSelected = plans.some((item) => item.date === formattedDate);
                                    let isApproved = selectedPlan?.isApproved;
                                    let isSuccess = false;
                                    let isWarning = false;

                                    if (isApproved) {
                                        isSuccess = true;
                                    } else {
                                        isWarning = true;
                                    }

                                    const selectedItem = dates.find((item) => item.date === format(day, "yyyy-MM-dd"));
                                    const dateId = selectedItem ? selectedItem.id : 0;
                                    const isCanceled = isCancel(dateId);
                                    const isSubstituted = isSubstitute(dateId);
                                    const classIndex = selectedItem ? selectedItem.classIndex : null;
                                    const isOnlySubstitute = selectedPlan?.description ? false : true
                                    return (
                                        <Day
                                            key={index}
                                            isWeekend={isWeekend}
                                            isSelected={isSelected}
                                            isSelectedDashed={isSelectedDash && !isCanceled && !isSubstituted}
                                            isSuccess={isSuccess}
                                            isWarning={isWarning}
                                            isCanceled={isCanceled}
                                            isSubstituted={isSubstituted}
                                            isOnlySubstitute={isOnlySubstitute}
                                            value={format(day, "d")}
                                            onClick={() => {
                                                if (isSelected && !isCanceled) {
                                                    gotoEdit(dateId, selectedPlan?.id, classIndex);
                                                } else {
                                                    if (isSelectedDash && !isCanceled) {
                                                        gotoAdd(dateId, classIndex);
                                                    }
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </Month>
                        );
                    })}
                </Calendar>
            </Main>
        </DefaultLayout>
    );
};

export default Planning;
